import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { AsyncView } from '@aboutbits/react-toolbox'
import { HeaderArea, HeaderTitle } from '../../../shared/header'
import { Error } from '../../../shared/error'
import { ContentArea } from '../../../shared/content'
import { useGetAllCostCenters } from '../../costCenter/db/costCenters'
import { RecordType, TimeTrackingRecord } from '../types'
import { useGetTimeTrackingRecords } from '../db/records'
import { useHasModulePermission } from '../../../auth/hooks'
import { useGetProjectsByIds } from '../../project/db/projects'
import { Module, Permission } from '../../../auth/types'
import { Help } from '../../../shared/help/Help'
import { translationsTimetracking } from '../translations/translationsTimeTracking'
import { TimeTrackingWidget } from './TimeTrackingWidget'
import {
  EnrichedTimeTracking,
  TimeTrackingOverviewList,
} from './TimeTrackingOverviewList'
import { TimeTrackingOverviewLoading } from './TimeTrackingOverviewLoading'

const TimeTrackingSuccess: React.FC<{
  data: TimeTrackingRecord[]
}> = ({ data }) => {
  const sortedRecords = data.sort((a, b) => {
    const timeCompare = b.time.localeCompare(a.time)
    if (timeCompare !== 0) {
      return timeCompare
    }
    return a.type === RecordType.WORK_START ? -1 : 1
  })

  const hasCreatePermissions = useHasModulePermission(
    Module.timeTacking,
    Permission.create
  )

  const { data: costCenters } = useGetAllCostCenters()
  const { data: projects } = useGetProjectsByIds(
    data
      .filter((timeTrackingRecord) => timeTrackingRecord.projectId !== null)
      .map((timeTrackingRecord) => timeTrackingRecord.projectId as string)
  )

  const enrichedData: EnrichedTimeTracking[] = sortedRecords.map(
    (timeTracking) => {
      const assignedCostCenter =
        timeTracking.costCenterId !== null && costCenters !== null
          ? costCenters.find(
              (costCenter) => costCenter.id === timeTracking.costCenterId
            )?.name
          : undefined

      const assignedProject =
        timeTracking.projectId !== null && projects !== null
          ? projects.find((project) => {
              return project.id === timeTracking.projectId
            })?.name
          : undefined

      return {
        ...timeTracking,
        costCenterName: assignedCostCenter,
        projectName: assignedProject,
      }
    }
  )

  return (
    <>
      {hasCreatePermissions && <TimeTrackingWidget data={sortedRecords} />}
      <TimeTrackingOverviewList data={enrichedData} />
    </>
  )
}

const TimeTrackingOverview: React.FC = () => {
  const intl = useIntl()
  const { data, error } = useGetTimeTrackingRecords()

  return (
    <>
      <HeaderArea>
        <HeaderTitle>
          <FormattedMessage
            {...translationsTimetracking.timeTrackingOverviewTitle}
          />
        </HeaderTitle>
      </HeaderArea>

      <ContentArea>
        <AsyncView
          data={data}
          error={error}
          renderLoading={
            <TimeTrackingOverviewLoading
              title={intl.formatMessage(
                translationsTimetracking.timeTrackingOverviewTitle
              )}
            />
          }
          renderSuccess={(data) => data && <TimeTrackingSuccess data={data} />}
          renderError={(error) => <Error text={error && error?.message} />}
        />
        <Help href="https://camid.eu" />
      </ContentArea>
    </>
  )
}

export { TimeTrackingOverview }
